import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Box, Paper, TableContainer, LinearProgress, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { Header } from "../../../components/global/Header";
import FormListToolbar from "../../../components/global/Toolbar";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from "react-router";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import SearchField from '../../../components/search/Search';
import { useProductionCategories } from './useProductionCategories';
import ErrorNotification from '../../../components/ErrorNotification/ErrorNotification';
import api from "../../../services/api";
import { useNotifier } from "muinotify";
import { exportToExcel } from '../../../components/export/exportToExcel';
import { exportToPdf } from '../../../components/export/exportToPdf';
import { exportToCsv } from '../../../components/export/exportToCsv';
import ProductionCategoriesDetails from './ProductionCategoriesDetails';

const ProductionCategories = () => {
  const { notifier } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const { production_categories, isLoading, error, mutate } = useProductionCategories(searchQuery);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedProductionCategories, setSelectedProductionCategories] = useState(null);
  const NAV_URL = `production_categories`;
  const URL = `production-category`;

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  function getRowId(row) {
    return row.id;
  }

  const handleClear = () => {
    setSearchQuery("");
    mutate(); // Refetch data after clearing search
  };

  let navigate = useNavigate();

  const handlePanelClose = () => {
    setSelectedProductionCategories(null);
  };

  const handleView = (production_categories) => {
    setSelectedProductionCategories(production_categories);
  };

  const handleEdit = (id) => {
    navigate(`/${NAV_URL}/update/${id}`);
  };

  const handleAdd = () => {
    navigate(`/${NAV_URL}/create`);
  };

  const handleDelete = useCallback((id) => {
    confirmAlert({
      title: 'Confirm to delete',
      message: 'Are you sure you want to delete this production?',
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try {
              await api.delete(`/${URL}/${id}`);
              notifier("Production category deleted successfully.", { variant: "success" });
              await mutate();
            } catch (err) {
              notifier("Failed to delete production category. " + err.message, { variant: "error" });
            }
          }
        },
        {
          label: 'No',
        }
      ]
    });
  }, [mutate, notifier]);


  const columnsToExtract = ['clientName', 'clientName', 'contactPerson', 'clientAddress', 'clientCity', 'clientPhone', 'clientEmail', 'clientNotes'];

  const handleExportToExcel = () => {
    exportToExcel(production_categories || [], columns, columnsToExtract, 'clients-list', 'Clients List');
  };

  const handleExportToPdf = () => {
    exportToPdf(production_categories || [], columns, columnsToExtract, 'clients-list');
  };

  const handleExportToCsv = () => {
    exportToCsv(production_categories || [], columns, columnsToExtract, 'clients-list');
  };

  const columns = [
    // { field: 'id', headerName: 'ID', flex: 1 },
    {
      field: 'prod_descr', headerName: 'Παραγωγή', flex: 1, headerAlign: 'center', renderCell: (params) => {
        return (
          <>
            <div>
              {params.value.p_descr}
            </div>
          </>
        );
      },
    },
    {
      field: 'pc_types_descr', headerName: 'Είδος παραγωγής', flex: 1, headerAlign: 'center', renderCell: (params) => {
        return (
          <>
            <div>
              {params.value.pct_descr}
            </div>
          </>
        );
      },
    },
    { field: 'pc_descr', headerName: 'Περιγραφή', flex: 1, headerAlign: 'center', },
    { field: 'pc_notes', headerName: 'Σημειώσεις', flex: 1, headerAlign: 'center', },
    // { field: 'p_aa', headerName: 'AA', flex: 1 },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      headerName: "Actions",
      headerAlign: 'center',
      getActions: (params) => [
        <GridActionsCellItem color="primary" icon={<EditOutlinedIcon />} label="Edit" onClick={() => handleEdit(params.row.id)} />,
        <GridActionsCellItem color="secondary" icon={<DeleteOutlineOutlinedIcon />} label="Delete" onClick={() => handleDelete(params.row.id)} />,
      ],
    },
  ];

  return (
    <Box m="20px" >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="ProductionCategories" module="ProductionCategories" subtitle="List of Production Categories" />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={selectedProductionCategories ? 8 : 12}>

          <FormListToolbar
            handleAdd={handleAdd}
            handleExportToExcel={handleExportToExcel}
            handleExportToPdf={handleExportToPdf}
            handleExportToCsv={handleExportToCsv}
          />
          <Box
            m="8px 0 0 0"
            height="80vh"
          >
            <Paper elevation={2} style={{ padding: '1rem', height: '100%', width: '100%' }}>
              <SearchField searchQuery={searchQuery} handleSearchChange={handleSearchChange} handleClear={handleClear} ml={2} mr={2} />
              {isLoading && <LinearProgress />}
              {error && <ErrorNotification ml={2} mr={2} error={error} />}
              <TableContainer style={{ height: '87%', width: '100%' }}>
                <DataGrid
                  rows={production_categories || []}
                  columns={columns}
                  getRowId={getRowId}
                  columnVisibilityModel={{
                    id: false,
                  }}
                  onRowClick={(params) => setSelectedProductionCategories(params.row)} // Set selected client on row click
                  sx={{
                    '& .MuiDataGrid-cell': {
                      borderRight: '1px solid #ddd',
                      borderBottom: 'none',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      borderRight: '1px solid #ddd',
                    },
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnsContainer': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '1px solid #e0e0e0',
                    },
                    '& .MuiTablePagination-root': {
                      color: '#000',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                  }}
                />
              </TableContainer>
            </Paper>
          </Box>
        </Grid>
        {selectedProductionCategories && (
          <Grid item xs={4}>
            <ProductionCategoriesDetails production_categories={selectedProductionCategories} onClose={handlePanelClose} />
          </Grid>
        )}
      </Grid>
    </Box>

  );
};

export default ProductionCategories;